import { GridColumn } from 'src/components/GridWrapper';

import { Grid } from '@progress/kendo-react-grid';
import { RefObject, useEffect, useMemo, useState } from 'react';
import GoToPoDetails from 'src/components/GoToPoDetails';
import AttachmentIconCell from 'src/components/custom-cells/AttachmentIconCell';
import GLCodeMismatchCell from 'src/components/custom-cells/GLCodeMismatchCell';
import InvoiceErrorCell from 'src/components/custom-cells/InvoiceErrorCell';
import PercentReceivedCell from 'src/components/custom-cells/PercentReceivedCell';
import PoItemsCompletedCell from 'src/components/custom-cells/PoItemsCompletedCell';
import AttachmentIconHeader from 'src/components/custom-headers/AttachmentIconHeader';
import ErrorIconHeader from 'src/components/custom-headers/ErrorIconHeader';
import {
  CancelPoPermissions,
  ClosePoPermissions,
  CompleteInvoicePermissions,
  DeleteInvoicePermissions,
  UnDeleteInvoicePermissions,
} from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import InvoiceIdCell from 'src/sections/view_invoices/cells/InvoiceIdCell';
import ViewInvoicesDocTotal from 'src/sections/view_invoices/cells/ViewInvoicesDocTotal';

const useViewInvoiceColumns: (tableRef: RefObject<Grid>, rows: any[]) => GridColumn<any>[] = (
  tableRef,
  rows
) => {
  const [gridSize, setGridSize] = useState(0);

  const isClosePoAccess = usePermission({ permissions: ClosePoPermissions, or: true });
  const isCompleteInvocieAccess = usePermission({
    permissions: CompleteInvoicePermissions,
    or: true,
  });
  const isDeleteInvoiceAccess = usePermission({ permissions: DeleteInvoicePermissions, or: true });
  const isUnDeleteInvoiceAccess = usePermission({
    permissions: UnDeleteInvoicePermissions,
    or: true,
  });
  const isCancelPoAccess = usePermission({ permissions: CancelPoPermissions, or: true });

  const showSelectedField = useMemo(
    () =>
      isCancelPoAccess ||
      isCompleteInvocieAccess ||
      isDeleteInvoiceAccess ||
      isClosePoAccess ||
      isUnDeleteInvoiceAccess,
    [
      isCancelPoAccess,
      isClosePoAccess,
      isCompleteInvocieAccess,
      isDeleteInvoiceAccess,
      isUnDeleteInvoiceAccess,
    ]
  );

  const columnFormat1Size = useMemo(
    () => (gridSize >= 1700 ? (gridSize - 121) * 0.076 : 120),
    [gridSize]
  );

  useEffect(() => {
    const handleResize = () => {
      if (tableRef?.current?.element) setGridSize(tableRef.current.element.offsetWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tableRef]);

  const columns: GridColumn<any>[] = useMemo(
    () => [
      ...(showSelectedField && rows.length > 0
        ? ([
            {
              field: 'selected',
              title: '',
              filter: 'boolean',
              width: '30px',
              format: '',
              menu: false,
            },
          ] as GridColumn<any>[])
        : []),
      {
        field: 'numOfAttachments',
        title: '',
        component: AttachmentIconCell,
        headerCell: AttachmentIconHeader,
        width: 40,
        menu: false,
      },
      {
        field: 'errors',
        title: '',
        component: InvoiceErrorCell,
        headerCell: ErrorIconHeader,
        width: 55,
        menu: false,
        sortable: false,
      },
      {
        field: 'poNum',
        title: 'PO #',
        filter: 'text',
        component: GoToPoDetails,
        width: columnFormat1Size,
        format: '',
        dataType: 'string',
      },
      {
        field: 'invoiceNumber',
        title: 'Invoice/Credit #',
        filter: 'text',
        component: InvoiceIdCell,
        width: columnFormat1Size,
        format: '',
        dataType: 'string',
      },
      {
        field: 'status',
        title: 'Status',
        filter: 'text',
        format: '',
        width: columnFormat1Size + 60,
        dataType: 'string',
      },
      {
        field: 'statusDate',
        title: 'Status Date',
        filter: 'date',
        format: '',
        width: columnFormat1Size,
        dataType: 'date',
      },
      {
        field: 'supplierName',
        title: 'Supplier Name',
        filter: 'text',
        format: '',
        width: columnFormat1Size + 10,
        dataType: 'string',
      },
      {
        field: 'poStatus',
        title: 'PO Status',
        filter: 'text',
        format: '',
        width: columnFormat1Size - 20,
        dataType: 'string',
      },
      {
        field: 'percentRecvByQuantity',
        title: '% Rec (by Qty)',
        filter: 'numeric',
        component: PercentReceivedCell,
        format: '',
        width: columnFormat1Size,
        filterFormat: 'p2',
        step: 0.01,
      },
      {
        field: 'percentRecvByDollar',
        title: '% Inv (by $)',
        filter: 'numeric',
        component: PercentReceivedCell,
        format: '',
        width: columnFormat1Size,
        dataType: 'number',
        filterFormat: 'p2',
        step: 0.01,
      },
      {
        field: 'currentDocTotal',
        title: 'Invoice Total',
        filter: 'numeric',
        format: '{0:c2}',
        width: columnFormat1Size,
        dataType: 'number',
        filterFormat: 'c2',
        step: 0.1,
        component: ViewInvoicesDocTotal,
      },
      {
        field: 'hasGlMismatchItems',
        title: 'GL Code Mismatch',
        filter: 'text',
        component: GLCodeMismatchCell,
        format: '',
        width: columnFormat1Size + 10,
        dataType: 'string',
      },
      {
        field: 'poItemsCompleted',
        title: 'PO Items Complete',
        filter: 'text',
        component: PoItemsCompletedCell,
        format: '',
        width: columnFormat1Size + 10,
        dataType: 'string',
      },
      {
        field: 'poDate',
        title: 'PO Date',
        filter: 'date',
        format: '',
        width: columnFormat1Size - 20,
        dataType: 'date',
      },
      {
        field: 'poSubject',
        title: 'PO Subject',
        filter: 'text',
        format: '',
        width: columnFormat1Size - 20,
        dataType: 'number',
      },
      {
        field: 'buyerName',
        title: 'Buyer Name',
        filter: 'text',
        format: '',
        width: columnFormat1Size,
        dataType: 'string',
      },
    ],
    [columnFormat1Size, rows.length, showSelectedField]
  );

  return columns;
};

export default useViewInvoiceColumns;
